import { useCallback, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcSweepsV1SweepsCutoffQueryServiceGetSweepsCutoff } from '@interxfi/api-client/generated/hooks';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import {
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNumber,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
} from '@interxfi/app-components/components/readonly-list/util';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Box, DEFAULT_BOX_PADDING, DEFAULT_BOX_PADDING_NO_HORIZONTAL } from '@interxfi/design-system/components/box/box';
import { PlainIconButton } from '@interxfi/design-system/components/button/plain-icon/plain-icon-button';
import { Countdown } from '@interxfi/design-system/components/countdown/countdown';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import InfoIcon from '@interxfi/design-system/icons/react/icon-info';
import { buildTooltipInteraction } from '@interxfi/design-system/util/interaction';
import type { IxcSweepsV1SweepsCutoff } from '@interxfi/types';
import { tryToGetTimeForDate } from '@interxfi/utilities/date/time-for-day';
import { getNumberOrUndefinedFromNullable } from '@interxfi/utilities/object/access';
import { PageTabRail } from '../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../components/router/router-tabbed-navigation';
import { getAssetDetailPath } from '../../../../../../features/asset/asset-routing';
import { SweepCutoffFundsTable } from '../../../../../../features/sweeps/cutoff/sweep-cutoff-funds-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/sweep/cutoff/_cutoff/')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.Sweeps,
      label: 'Cutoff',
      order: 2,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.Sweeps>,
  head: () => getPageHead('Sweep Cutoff'),
  component: SweepsCutoffDetails,
});

function getListItems(data: IxcSweepsV1SweepsCutoff | undefined) {
  return {
    left: [
      getReadonlyListItemDate('cutoff', 'Cutoff', data?.cutoff, 'timestamp_iso_ms'),
      getReadonlyListItemDate('tradingDay', 'Trading Day', data?.tradingDay, 'date_iso'),
      getReadonlyListItemNumber('offsetMinutes', 'Offset Minutes', getNumberOrUndefinedFromNullable(data, 'offsetMinutes'), undefined, {
        action: buildTooltipInteraction(
          'This is subtracted from the close time of the limiting factor to determine the cutoff.',
          <PlainIconButton aria-label="More information">
            <InfoIcon aria-hidden width={16} />
          </PlainIconButton>,
          { maxWidth: 136 },
        ),
      }),
      getReadonlyListItemDate(
        'familyClose',
        'Fund Family Close',
        tryToGetTimeForDate(data?.tradingDay, data?.sessionClose, data?.sessionTimezone)?.toString(),
        'time_hh_mm',
      ),
    ] as const satisfies ReadonlyListItem[],
    right: [
      ...(data?.limitingFactor?.assetId
        ? [getReadonlyListItemId('limitingFactor.assetId', 'Target', data.limitingFactor.assetId, getAssetDetailPath(data.limitingFactor.assetId))]
        : []),
      getReadonlyListItemDate(
        'limitingFactor.close',
        'Close',
        tryToGetTimeForDate(data?.tradingDay, data?.limitingFactor?.close, data?.limitingFactor?.timezone)?.toString(),
        'time_hh_mm',
      ),
      getReadonlyListItemTranslatedString('limitingFactor.source', 'Asset Hours Source', {
        namespace: 'ixcSweepsV1',
        enumName: 'IxcSweepsV1CutoffSource',
        value: data?.limitingFactor?.source,
      }),
      ...(data?.limitingFactor?.exception
        ? [
            getReadonlyListItemString('limitingFactor.exception.name', 'Exception Name', data.limitingFactor.exception.name),
            getReadonlyListItemString('limitingFactor.exception.exceptionId', 'Exception ID', data.limitingFactor.exception.exceptionId),
          ]
        : []),
    ] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load sweep cutoff times', 'view sweep cutoff times');

function SweepsCutoffDetails() {
  const [asOf, setAsOf] = useState(new Date());
  const { data, error, isPending } = useIxcSweepsV1SweepsCutoffQueryServiceGetSweepsCutoff({ asOf: asOf.toISOString() });
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.cutoff), [data?.cutoff]);
  const cutoffAsDate = useMemo(() => (data?.cutoff?.cutoff ? new Date(data.cutoff.cutoff) : undefined), [data?.cutoff]);
  const handleCountdownZero = useCallback(() => {
    const cutoffOffset = (data?.cutoff?.offsetMinutes || 0) * 60 * 1_000 + 1;

    setAsOf(new Date(Date.now() + cutoffOffset));
  }, [data?.cutoff?.offsetMinutes]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageTabRail
            actions={cutoffAsDate ? <Countdown onZero={handleCountdownZero} to={cutoffAsDate} /> : null}
            group={TabbedNavigationGroup.Sweeps}
          />

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: (
                <Box padding={DEFAULT_BOX_PADDING_NO_HORIZONTAL}>
                  <VerticalStack gap={4}>
                    <Typography.Level3>Data</Typography.Level3>
                    <AppReadonlyList isPending={showPending} items={listItems.left} />
                  </VerticalStack>
                </Box>
              ),
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: (
                <Box backgroundColor="neutral-xlt" borderRadius="8px" padding={DEFAULT_BOX_PADDING}>
                  <VerticalStack gap={4}>
                    <Typography.Level3>Limiting Factor</Typography.Level3>

                    <AppReadonlyList isPending={showPending} items={listItems.right} />
                  </VerticalStack>
                </Box>
              ),
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={7}>
          <Typography.Level3>Closes for All Target Funds</Typography.Level3>

          <SweepCutoffFundsTable funds={data?.cutoff?.funds} isPending={showPending} tradingDay={data?.cutoff?.tradingDay} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
